import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  business: "",
  site_url: "",
  message: "",
  interest: "website_improve",
  website: "",
};

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

export default function ReviewForm() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...EMPTY, site_url: (params.get("url") || "").trim() });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const update = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = "Please add your name.";
    if (!isValidEmail(form.email)) next.email = "Please enter a valid email address.";
    if (!form.site_url.trim()) next.site_url = "Add your website address so we can review it.";
    if (Object.keys(next).length) {
      setErrors(next);
      toast.error("Please check the highlighted fields.");
      return;
    }
    if (!consent) {
      toast.error("Please agree to receive emails and texts about your review.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSubmitted(true);
      toast.success("Review request sent. We'll take a look shortly.");
      setForm((f) => ({ ...f, website: "" }));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send right now. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send right now.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="review-form-success"
        className="rounded-3xl border border-white/[0.07] bg-[#101012] p-8 sm:p-10 text-center"
      >
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
          <CheckCircle size={26} weight="regular" />
        </div>
        <h3 className="mt-5 font-display text-2xl sm:text-3xl tracking-tight text-white">
          Got it — your review is queued.
        </h3>
        <p className="mt-3 text-zinc-300 text-[14.5px] leading-relaxed max-w-md mx-auto">
          We look at every site personally and reply within one business
          day with what we'd change and what it would cost. No charge for the
          review, no obligation either way.
        </p>
        <button
          type="button"
          onClick={() => {
            setSubmitted(false);
            setConsent(false);
            setForm({ ...EMPTY });
          }}
          data-testid="review-form-reset"
          className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 bg-white/[0.03] text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors text-[13.5px]"
        >
          Send another site
        </button>
      </div>
    );
  }

  return (
    <motion.form
      onSubmit={submit}
      noValidate
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="review-form"
      className="rounded-3xl border border-white/[0.07] bg-[#101012] p-6 sm:p-8 relative overflow-hidden"
    >
      <div className="text-[11.5px] tracking-[0.2em] uppercase text-zinc-400">
        Request your free review
      </div>
      <p className="mt-3 text-[14px] text-zinc-300 leading-relaxed">
        Send us the link and we'll reply within one business day with what we'd
        change — and what it would cost. The review is free and there's no
        obligation.
      </p>

      {/* Honeypot — hidden from real users, attractive to bots. */}
      <div
        aria-hidden="true"
        style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}
      >
        <label htmlFor="review-website-field">Website (leave blank)</label>
        <input
          id="review-website-field"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={update("website")}
        />
      </div>

      <div className="mt-6 grid gap-4">
        <ReviewField
          label="Your Website Address"
          testid="review-site-url"
          value={form.site_url}
          onChange={update("site_url")}
          placeholder="yourbusiness.com"
          required
          error={errors.site_url}
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <ReviewField
            label="Your Name"
            testid="review-name"
            value={form.name}
            onChange={update("name")}
            placeholder="Your full name"
            required
            error={errors.name}
          />
          <ReviewField
            label="Email"
            testid="review-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@example.com"
            required
            error={errors.email}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <ReviewField
            label="Phone"
            testid="review-phone"
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            placeholder="(602) 390-5623"
          />
          <ReviewField
            label="Business Name"
            testid="review-business"
            value={form.business}
            onChange={update("business")}
            placeholder="CleanCurrent LLC"
          />
        </div>

        <div>
          <ReviewLabel htmlFor="review-message">What do you want improved?</ReviewLabel>
          <textarea
            id="review-message"
            data-testid="review-message"
            value={form.message}
            onChange={update("message")}
            rows={4}
            placeholder="My homepage looks dated on phones and the quote form is hard to use. I'd like better service pages too."
            className="mt-2 w-full bg-[#0a0a0c] border border-white/10 rounded-xl px-4 py-3 text-[14.5px] text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 transition-colors"
          />
        </div>
      </div>

      <label
        htmlFor="review-consent"
        className="mt-6 flex items-start gap-3 cursor-pointer select-none group"
      >
        <span className="relative flex-shrink-0 mt-0.5">
          <input
            id="review-consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="review-consent"
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className="block h-[18px] w-[18px] rounded-[5px] border border-white/20 bg-[#0a0a0c] group-hover:border-white/40 peer-focus-visible:ring-2 peer-focus-visible:ring-white/30 peer-checked:bg-white peer-checked:border-white transition-colors duration-150"
          />
          <svg
            aria-hidden="true"
            viewBox="0 0 12 10"
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[8px] w-[10px] opacity-0 peer-checked:opacity-100 transition-opacity duration-150"
          >
            <path d="M1 5.2 4.3 8.5 11 1.5" fill="none" stroke="#0a0a0c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <span className="text-[13px] text-zinc-300 leading-relaxed group-hover:text-white">
          I agree to receive emails and SMS text messages from WebJesters about my
          review request. Consent isn&apos;t a condition of purchase. Msg &amp; data
          rates may apply; frequency varies. Reply STOP to opt out, HELP for help.
        </span>
      </label>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="text-[12.5px] text-zinc-400 leading-relaxed">
          By submitting, you agree to WebJesters'{" "}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-zinc-200 underline underline-offset-4 hover:text-white">
            Terms
          </a>{" "}
          and{" "}
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-zinc-200 underline underline-offset-4 hover:text-white">
            Privacy Policy
          </a>
          .
        </div>
        <button
          type="submit"
          disabled={submitting || !consent}
          data-testid="review-submit"
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {submitting ? "Sending…" : "Request my free review"}
          <ArrowUpRight size={16} weight="bold" />
        </button>
      </div>
    </motion.form>
  );
}

function ReviewLabel({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="text-[12px] tracking-[0.16em] uppercase text-zinc-400">
      {children}
    </label>
  );
}

function ReviewField({ label, testid, value, onChange, placeholder, type = "text", required, error }) {
  return (
    <div>
      <ReviewLabel htmlFor={testid}>
        {label}
        {required && <span className="text-brand-400 ml-1">*</span>}
      </ReviewLabel>
      <input
        id={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${testid}-error` : undefined}
        className="mt-2 w-full bg-[#0a0a0c] border border-white/10 rounded-xl px-4 py-3 text-[14.5px] text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 transition-colors"
      />
      {error && (
        <p id={`${testid}-error`} role="alert" data-testid={`${testid}-error`} className="mt-2 text-[12.5px] text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
